/*
 * Copyright (c) 2021 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import MockAdapter from 'axios-mock-adapter';
import { BaseFeatureMock } from '../mockServer/featureMock';
import {
    DocType,
    DocumentResource,
    DocumentsListDto,
    DocumentsListRequestParams,
    UnknownDocumentContent,
} from './documentsClient.typings';
import {
    correctionDocumentResource,
    creditPayDocumentResource,
    documentListItemDtoFactory,
    documentsGeneralInfoFactory,
    itemsFactory,
    moneyBackDocumentResource,
    openShiftDocumentResource,
    prepayDocumentResource,
    rollBackDocumentResource,
    saleDocumentResource,
    zReportDocumentResource,
} from './documentsFactories';

const DOCUMENTS_LIST_URL = /\/1.0.0\/documents$/;
const DOCUMENT_CARD_DETAILS_URL: RegExp = /\/documents\/(\w+)\/card/;
const DOCUMENT_CARD_PICT_URL = /\/1.0.0\/documents\/cardPict$/;

class DocumentsFeatureMock extends BaseFeatureMock {
    private documentCardType = DocType.Sale;
    private documentTypesById = new Map<string, DocType>();
    private nextDocumentId = 0;

    public readonly listDocuments = this.route<DocumentsListDto, DocumentsListRequestParams>({
        method: 'post',
        url: DOCUMENTS_LIST_URL,
        defaultResponse: (config) => {
            const body: DocumentsListRequestParams = JSON.parse(config.data);
            const documents = documentListItemDtoFactory
                .list({ count: body.limit === 10000 ? 4 : body.limit })
                .map((document) => {
                    const documentId = `mock78798GH87897_${this.nextDocumentId++}`;
                    this.documentTypesById.set(documentId, document.docTypeId);

                    return {
                        ...document,
                        documentId,
                        documentIdHash: `${documentId}_hash`,
                    };
                });
            const data: DocumentsListDto = {
                afterKeyCreatedAtUtc: '2019-11-10T01:01:00.000Z',
                afterKeyId: '1431',
                documents,
            };

            return [200, data];
        },
        delayMs: 300,
    });

    public readonly documentCard = this.route<DocumentResource>({
        method: 'get',
        url: DOCUMENT_CARD_DETAILS_URL,
        defaultResponse: (config) => {
            const documentId = config.url.match(DOCUMENT_CARD_DETAILS_URL)?.[1];
            const documentType = (documentId && this.documentTypesById.get(documentId)) ?? this.documentCardType;
            const data: DocumentResource = {
                ...documentsGeneralInfoFactory.item(),
                ...(documentId ? { documentId } : {}),
                content: this.getDocumentContent(documentType),
            };

            return [200, data];
        },
        delayMs: () => Math.random() * 1000,
    });

    public readonly documentCardPict = this.route<ArrayBuffer>({
        method: 'get',
        url: DOCUMENT_CARD_PICT_URL,
        defaultResponse: () => new Uint8Array([0xff, 0xd8, 0xff, 0xd9]).buffer,
        delayMs: () => Math.random() * 1000,
    });

    public setDocumentCardType(docType: DocType): void {
        this.documentCardType = docType;
    }

    protected onReset(): void {
        this.documentCardType = DocType.Sale;
        this.documentTypesById.clear();
        this.nextDocumentId = 0;
        documentListItemDtoFactory.resetCount();
        documentsGeneralInfoFactory.resetCount();
        itemsFactory.resetCount();
    }

    private getDocumentContent(docType: DocType): UnknownDocumentContent {
        itemsFactory.resetCount();

        switch (docType) {
            case DocType.OpenShift:
                return openShiftDocumentResource.item();
            case DocType.ZReport:
                return zReportDocumentResource.item();
            case DocType.MoneyBack:
                return moneyBackDocumentResource.item();
            case DocType.RollBack:
                return rollBackDocumentResource.item();
            case DocType.Correction:
                return correctionDocumentResource.item();
            case DocType.Prepayment:
                return prepayDocumentResource.item();
            case DocType.CreditPay:
                return creditPayDocumentResource.item();
            case DocType.Sale:
            default:
                return saleDocumentResource.item();
        }
    }
}

export const documentsMock = new DocumentsFeatureMock();

/** Backward-compat shim - dev mock mode (mockAdapter.ts) keeps calling this untouched. */
export function addDocumentsMockApi(mockAdapter: MockAdapter): void {
    documentsMock.register(mockAdapter, { delay: true });
}
